/**
 * 2026-06-04 icing #4 — per-dim filter summary strings.
 *
 * Renders the right-aligned summary that the rail's picker rows show
 * (e.g. "Allianz +2", "A+ ↑", "$100k", "7, 10", "NY, CA +3"). When no
 * filter is active for a dim, returns the literal string "All" — the
 * caller decides how to render that "empty" state (the rates rail
 * template paints it muted+italic via the .rf-allnone class on the host).
 *
 * Dispatcher lives here (rather than inside the picker host) so the
 * single source of truth is co-located with DIM_LABELS. The host is
 * pure presentational chrome and stays stateless.
 *
 * Tested via the rail-as-summary E2E spec.
 */

import type { DimKey } from './rates-filter-panel.service';
import type { FilterStateSlot } from './rates-filter-state.service';

/**
 * Extra (non-FilterStateSlot) inputs the summarizer needs for FA-only
 * dims, which live on dedicated signals rather than the per-tab slot.
 *
 * Pass `undefined` for any field the caller doesn't have — the dispatcher
 * treats missing FA fields as empty (returns 'All').
 */
export interface DimSummaryContext {
  /** FA Rate Term selections (years) — from RatesFilterStateService.faRateTerms(). */
  faRateTerms?: ReadonlyArray<number>;
  /** FA Surrender Years selections — from RatesFilterStateService.faSurrYears(). */
  faSurrYears?: ReadonlyArray<number>;
  /** FA Bonus Years selections — from RatesFilterStateService.faBonusYears(). */
  faBonusYears?: ReadonlyArray<number>;
  /** FA MVA toggle — from RatesFilterStateService.faMva(). */
  faMva?: boolean;
  /** FA Qualified toggle — from RatesFilterStateService.faQualified(). */
  faQualified?: boolean;
}

/** Marker the host can check to render the empty state visual treatment. */
export const EMPTY_SUMMARY = 'All';

/**
 * Compute the summary string for a single dim against the active filter
 * slot. Returns 'All' when no filter is active.
 *
 * Length convention for multi-select dims:
 *   · 0 selected             → 'All'
 *   · 1                      → just the value (e.g. "S&P 500")
 *   · 2                      → joined with ', ' (e.g. "S&P 500, MSCI EAFE")
 *   · 3+                     → "first +N" (e.g. "S&P 500 +2")
 */
export function summarize(
  dim: DimKey,
  slot: FilterStateSlot,
  ctx: DimSummaryContext = {},
): string {
  switch (dim) {
    // ── Multi-select string lists ───────────────────────────────
    case 'amBest':
      return summarizeMulti(slot.selectedAmBest);
    case 'premiumType':
      return summarizeMulti(slot.selectedPremiumTypes);
    case 'index':
      return summarizeMulti(slot.selectedIndexes);
    case 'crediting':
      return summarizeMulti(slot.selectedStrategyNames);
    case 'combo':
      return summarizeMulti(slot.selectedStrategyCombos);
    case 'mktg':
      return summarizeMulti(slot.selectedCreditingStrategies);
    case 'riders':
      return summarizeMulti(slot.selectedRiders);
    case 'surrYears':
      return summarizeMulti(slot.selectedSurrYears);

    // ── Numeric (single value) ─────────────────────────────────
    case 'premium':
      return slot.contributionAmount == null
        ? EMPTY_SUMMARY
        : '$' + slot.contributionAmount.toLocaleString();

    // ── States (multi-select with rollup) ──────────────────────
    case 'states':
      return summarizeStates(slot.selectedStates);

    // ── Booleans (per-tab toggles) ─────────────────────────────
    case 'rop':       return slot.filterRop       ? 'on' : EMPTY_SUMMARY;
    case 'bonus':     return slot.filterBonus     ? 'on' : EMPTY_SUMMARY;
    case 'inverse':   return slot.filterInverse   ? 'on' : EMPTY_SUMMARY;
    case 'mva':       return ctx.faMva            ? 'on' : EMPTY_SUMMARY;
    case 'owner':     return slot.filterOwner     ? 'on' : EMPTY_SUMMARY;
    case 'annuitant': return slot.filterAnnuitant ? 'on' : EMPTY_SUMMARY;

    // ── FA-specific (live on dedicated signals, not the slot) ──
    case 'faRateTerm':
      return summarizeMulti((ctx.faRateTerms ?? []).map(String));
    case 'faSurrYears':
      return summarizeMulti((ctx.faSurrYears ?? []).map(String));
    case 'faBonusYears':
      return summarizeMulti((ctx.faBonusYears ?? []).map(String));
    case 'faMva':
      return ctx.faMva ? 'on' : EMPTY_SUMMARY;
    case 'faQualified':
      return ctx.faQualified ? 'on' : EMPTY_SUMMARY;

    // 'carrier' isn't summarized here — the rates rail's Company &
    // Product summary lives on companyFilter (companies + products),
    // not the slot. The rail computes it inline.
    case 'carrier':
      return EMPTY_SUMMARY;
  }
}

// ── Reusable formatters ─────────────────────────────────────────

export function summarizeMulti(arr: ReadonlyArray<string>): string {
  if (arr.length === 0) return EMPTY_SUMMARY;
  if (arr.length === 1) return arr[0];
  if (arr.length === 2) return `${arr[0]}, ${arr[1]}`;
  return `${arr[0]} +${arr.length - 1}`;
}

export function summarizeStates(arr: ReadonlyArray<string>): string {
  if (arr.length === 0) return EMPTY_SUMMARY;
  if (arr.length === 1) return arr[0];
  if (arr.length === 2) return `${arr[0]}, ${arr[1]}`;
  return `${arr[0]}, ${arr[1]} +${arr.length - 2}`;
}

/** Tiny helper for the host template — true when summarize() returned the
 *  literal empty marker. Keeps the host from having to import the constant. */
export function isEmptySummary(s: string): boolean {
  return s === EMPTY_SUMMARY;
}
